import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  SvgIcon,
  Box,
  Button,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import {
  FavoriteBorderOutlined,
  ShoppingCartOutlined as ShoppingCartOutlinedIcon,
  CachedOutlined as CachedOutlinedIcon,
} from "@mui/icons-material";

import Tooltip from "../../../../components/Tooltip";
import IconButton from "../../../../components/IconButton";
import { useProductAnalyzer } from "../../../../contexts/productAnalyzerContext";

import {
  IProductHistory,
  TProductDetail,
} from "../../../../features/productAnalyzer/productAnalyzer.types";
import { Box as BoxIcon, Doc } from "../../../../utils";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { selectPrices } from "../../../../features/productAnalyzer/productAnalyzer.selector";
import { PurchaseMenu, ReplenishMenu } from "../../../../components";
import {
  addProductHistory,
  updateProductDetail,
} from "../../../../features/productAnalyzer/productAnalyzer.slice";
import { updateProduct } from "../../../../services/apiV2";
import { Notifications } from "../../../../constants";
import QuickListModal from "./QuickListModal";

type ProductDetailBtnGroupProps = {
  productDetail: TProductDetail;
};

const ProductDetailBtnGroup = ({
  productDetail,
}: ProductDetailBtnGroupProps) => {
  const dispatch = useAppDispatch();
  const { salePrice, costPrice } = useAppSelector(selectPrices);
  const isSmallScreen = useMediaQuery("(max-width:419px)");

  const [isUpdatingFavorite, setIsUpdatingFavorite] = useState<boolean>(false);
  const [purchaseMenuEl, setPurchaseMenuEl] = useState<null | HTMLElement>(
    null,
  );
  const [replenishMenuEl, setReplenishMenuEl] = useState<null | HTMLElement>(
    null,
  );
  const isPurchaseMenuOpened = Boolean(purchaseMenuEl);
  const isReplenishMenuOpened = Boolean(replenishMenuEl);

  const { asin, isFavorite, isPurchased, isReplenished } = productDetail;

  const handleFavoriteClick = () => {
    setIsUpdatingFavorite(true);
    updateProduct({ asin, isFavorite: !isFavorite })
      .then(() => {
        dispatch(updateProductDetail({ isFavorite: !isFavorite }));
      })
      .catch((e) => {
        console.error(e);
        enqueueSnackbar(Notifications.PA_UPDATE_ERROR, { variant: "error" });
      })
      .finally(() => {
        setIsUpdatingFavorite(false);
      });
  };

  const handleAddHistory = (data: IProductHistory) => {
    dispatch(addProductHistory(data));
  };

  const { openQuickList: contextOpenQuickList, setOpenQuickList } =
    useProductAnalyzer();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isListingOnAmazon, setIsListingOnAmazon] = useState<boolean>(false);
  const [quickListOpen, setQuickListOpen] = useState<boolean>(
    searchParams.get("openQuickList") === "true",
  );

  useEffect(() => {
    if (contextOpenQuickList) {
      setQuickListOpen(true);
      setOpenQuickList(false);
    }
  }, [contextOpenQuickList]);

  useEffect(() => {
    if (searchParams.get("openQuickList") === "true") {
      setQuickListOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("openQuickList");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams]);

  const handleQuickListClick = () => {
    setQuickListOpen(true);
  };

  return (
    <>
      <Tooltip
        title={
          <Box display={"flex"} flexDirection={"column"}>
            <strong>Package Dimensions</strong>
            <span>
              Width: {productDetail.packageDimensions?.width}
              <br />
            </span>
            <span>
              Height: {productDetail.packageDimensions?.height}
              <br />
            </span>
            <span>
              length: {productDetail.packageDimensions?.length}
              <br />
            </span>
            <span>Weight: {productDetail.packageDimensions?.weight}</span>
          </Box>
        }
      >
        <IconButton size="small">
          <Box
            component="img"
            src={BoxIcon}
            alt="Package Dimensions"
            sx={{
              width: isSmallScreen ? "14px" : "16px",
              height: isSmallScreen ? "14px" : "16px",
            }}
          />
        </IconButton>
      </Tooltip>
      <Tooltip
        title={
          <Box>
            {productDetail.aboutDetails?.map((detail: any, index: number) => (
              <Box key={`${detail}-${index}`}>
                {detail}
                <br />
                <br />
              </Box>
            ))}
          </Box>
        }
        placement="bottom"
        slotProps={{
          tooltip: {
            sx: {
              maxWidth: "none",
            },
          },
        }}
      >
        {/* <IconButton size="small">
          <SvgIcon
            component={DescriptionSharpIcon}
            sx={{ fontSize: "15px", color: "#ffffff" }}
            titleAccess="Item Description"
          />
        </IconButton> */}
        <IconButton size="small">
          <Box
            component="img"
            src={Doc}
            alt="Doc"
            sx={{
              width: isSmallScreen ? "14px" : "16px",
              height: isSmallScreen ? "14px" : "16px",
            }}
          />
        </IconButton>
      </Tooltip>
      {/* <Tooltip
        title={
          <Box display={"flex"} flexDirection={"column"}>
            <strong>Item Dimensions</strong>
            <br />
            <span>
              Width: {productDetail?.itemDimensions?.width}
              <br />
            </span>
            <span>
              Height: {productDetail?.itemDimensions?.height}
              <br />
            </span>
            <span>
              length: {productDetail?.itemDimensions?.length}
              <br />
            </span>
            <span>Weight: {productDetail?.itemDimensions?.weight}</span>
          </Box>
        }
      >
        <IconBtn variant="contained">
          <img src={Doc} alt="copy" className="w-full" />
        </IconBtn>
      </Tooltip> */}
      {/* <Tooltip title="Quick List"> */}
        <Button
          onClick={handleQuickListClick}
          disabled={isListingOnAmazon}
          variant="outlined"
          size="small"
          sx={{
            width: isSmallScreen ? "55px" : "70px",
            height: "27px",
            px: 0,
            py: 0,
            bgcolor: "#EFF6FF",
            border: "1px solid rgba(15, 86, 202, 0.5)",
            borderRadius: "6px",
            color: "#0F56CA",
            textTransform: "none",
            fontFamily: "Inter, sans-serif",
            fontSize: isSmallScreen ? "10px" : "12px",
            lineHeight: isSmallScreen ? "14px" : "18px",
            fontWeight: 500,
            letterSpacing: "0px",
            textAlign: "center",
            minWidth: isSmallScreen ? "55px" : "70px",
            "&:hover": {
              bgcolor: "#EFF6FF",
              border: "1px solid rgba(15, 86, 202, 0.5)",
            },
          }}
        >
            Quick List
        </Button>
      {/* </Tooltip> */}
      {/* <Tooltip
        title={
          <Box display={"flex"} flexDirection={"column"}>
            Search for product title on Google
          </Box>
        }
      >
        <IconButton
          onClick={() => window.open(productDetail.googleRedirect, "new")}
          size="small"
        >
          <img src={GoogleRe} alt="Search on Google" className="w-full" />
        </IconButton>
      </Tooltip>
      <Tooltip
        title={
          <Box display={"flex"} flexDirection={"column"}>
            Search for product on Walmart
          </Box>
        }
      >
        <IconButton
          onClick={() =>
            window.open(
              `https://walmart.com/search?q=${encodeURIComponent(
                productDetail.title
              )}`,
              "new"
            )
          }
          size="small"
        >
          <img src={Spin} alt="copy" className="w-full" />
        </IconButton>
      </Tooltip> */}
      <Tooltip title={"Add purchased quantity"}>
        <IconButton
          size="small"
          color={isPurchased ? "secondary" : "default"}
          id="purchase-button"
          aria-controls={isPurchaseMenuOpened ? "purchase-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={isPurchaseMenuOpened ? "true" : undefined}
          onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
            setPurchaseMenuEl(event.currentTarget)
          }
        >
          <ShoppingCartOutlinedIcon
            sx={{ fontSize: isSmallScreen ? "14px" : "16px" }}
            titleAccess="Add purchased quantity"
          />
        </IconButton>
      </Tooltip>
      <Tooltip title={"Add replenished quantity"}>
        <IconButton
          size="small"
          color={isReplenished ? "secondary" : "default"}
          id="replenish-button"
          aria-controls={isReplenishMenuOpened ? "replenish-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={isReplenishMenuOpened ? "true" : undefined}
          onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
            setReplenishMenuEl(event.currentTarget)
          }
        >
          <CachedOutlinedIcon
            sx={{ fontSize: isSmallScreen ? "14px" : "16px" }}
            titleAccess="Add replenished quantity"
          />
        </IconButton>
      </Tooltip>
      <Tooltip title={isFavorite ? "Remove from favorite" : "Add to favorite"}>
        <IconButton
          loading={isUpdatingFavorite}
          onClick={handleFavoriteClick}
          size="small"
          color={isFavorite ? "secondary" : "default"}
        >
          <SvgIcon
            component={FavoriteBorderOutlined}
            sx={{ fontSize: isSmallScreen ? "16px" : "18px" }}
          />
        </IconButton>
      </Tooltip>
      <PurchaseMenu
        open={isPurchaseMenuOpened}
        anchorEl={purchaseMenuEl}
        meta={{ asin, salePrice, costPrice }}
        onClose={() => setPurchaseMenuEl(null)}
        onPurchase={handleAddHistory}
      />
      <ReplenishMenu
        open={isReplenishMenuOpened}
        anchorEl={replenishMenuEl}
        meta={{ asin, salePrice, costPrice }}
        onClose={() => setReplenishMenuEl(null)}
        onReplenish={handleAddHistory}
      />
      <QuickListModal
        open={quickListOpen}
        onClose={() => setQuickListOpen(false)}
        asin={asin}
        imageURL={productDetail.imageURL}
        title={productDetail.title}
        defaultPrice={salePrice}
      />
    </>
  );
};

export default ProductDetailBtnGroup;
